import { z } from 'zod';

/**
 * Request/response contracts shared by the client, game server and admin panel.
 * Nothing in this package may reference server-only data (IPs, hashes, staff notes).
 */

export const USERNAME_RE = /^[A-Za-z0-9_]{3,16}$/;

export const usernameSchema = z
  .string()
  .trim()
  .regex(USERNAME_RE, 'Use 3–16 letters, numbers or underscores');

export const passwordSchema = z
  .string()
  .min(10, 'Use at least 10 characters')
  .max(200, 'Use at most 200 characters');

export const emailSchema = z.string().trim().toLowerCase().email('Enter a valid email address').max(254);

export const birthDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter your full date of birth');

/**
 * Which emails are needed depends on age (checked on the server):
 * 14 and under give only a parent's email; 15–16 give their own and a parent's; 17+ their own.
 */
export const registerSchema = z.object({
  username: usernameSchema,
  email: emailSchema.optional(),
  parentEmail: emailSchema.optional(),
  password: passwordSchema,
  birthDate: birthDateSchema,
  inviteCode: z.string().trim().max(20).optional(),
  /** Set when an adult signs up from a parent-consent link. */
  consentToken: z.string().max(100).optional(),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  /** Username or email. */
  login: z.string().trim().min(1).max(254),
  password: z.string().min(1).max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const displayNameSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9 ]{3,16}$/, 'Use 3–16 letters, numbers or spaces')
  .refine((v) => !/\s{2,}/.test(v), 'Use single spaces only');

const hexColor = z.string().regex(/^#[0-9a-f]{6}$/i);

export const appearanceSchema = z.record(z.string().max(32), z.union([hexColor, z.string().max(32)]));

export const createCharacterSchema = z.object({
  characterType: z.string().max(32),
  species: z.string().max(32).optional(),
  displayName: displayNameSchema,
  appearance: appearanceSchema,
});
export type CreateCharacterInput = z.infer<typeof createCharacterSchema>;

export interface CharacterTypeDto {
  key: string;
  name: string;
  appearanceOptions: Record<string, string[]>;
  species: { key: string; name: string; render: Record<string, unknown> }[];
}

export interface CharacterDto {
  id: number;
  displayName: string;
  characterType: string;
  species: string | null;
  appearance: Record<string, string>;
  equipped: Record<string, unknown>;
}

export interface ProgressDto {
  xp: number;
  level: number;
  levelXp: number;
  nextLevelXp: number | null;
}

export interface MeDto {
  user: { id: number; username: string; isStaff: boolean };
  character: CharacterDto | null;
  csrfToken: string;
  wallet?: Record<string, number>;
  progress?: ProgressDto;
  memberUntil?: string | null;
  emailVerified?: boolean;
  ageBracket?: string | null;
  canBeParent?: boolean;
  /** Rules a parent set (or the age bracket requires) for this player. */
  family?: { supervised: boolean; freeChat: boolean; disabledGames: string[]; friendApproval: boolean };
  /** 'bubble' (ages 8–14): show Bomb Squad as Bubble Squad. */
  squadBrand?: 'bubble' | 'bomb';
}

export interface ApiErrorBody {
  error: { code: string; message: string; fields?: Record<string, string> };
}

// ---------------------------------------------------------------------------
// Realtime (Colyseus) messages
// ---------------------------------------------------------------------------

export const ROOM_TOWN = 'town';
export const ROOM_BEACH = 'beach';

/** Client → server: "please walk me to this point". Positions are never sent by the client. */
export const moveIntentSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
});
export type MoveIntent = z.infer<typeof moveIntentSchema>;

export const MSG = {
  moveTo: 'move_to',
  /** Client → server: free text ({ text }) or a preset ({ phraseId }). */
  chat: 'chat',
  quick: 'quick',
  emote: 'emote',
  /** Server → clients: someone said something ({ id, text }). */
  say: 'say',
  /** Server → clients: someone emoted ({ id, emote }). */
  emoted: 'emoted',
  /** Server → one client: achievements/challenges/collections just completed. */
  progress: 'progress',
  /** Server → client: a request was refused (rate limit, invalid). */
  notice: 'notice',
  /** Haunted House: client → server { key } to catch a ghost; server → client the ghosts already found / a catch. */
  findGhost: 'find_ghost',
  ghosts: 'ghosts',
  ghostFound: 'ghost_found',
  /** Server → client: a friend invited you to a Squad game ({ roomId, from }). */
  squadInvite: 'squad_invite',
} as const;

/** Squad mode quick calls. Clients show them in their own wording (Bubble or Bomb Squad). */
export const SQUAD_CALLS = ['danger', 'safe', 'flag', 'nice', 'oops', 'help'] as const;
export type SquadCall = (typeof SQUAD_CALLS)[number];

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface AreaLayout {
  key: string;
  name: string;
  width: number;
  height: number;
  spawn: { x: number; y: number };
  /** Walk speed in pixels per second. */
  speed: number;
  /** Union of walkable rectangles. */
  walkable: Rect[];
  obstacles: Rect[];
  /** Clickable places that lead somewhere (another area, a shop, a minigame). */
  hotspots: { key: string; label: string; rect: Rect; action: string; event?: string }[];
  /** Set for areas that only open while an event runs. */
  event?: string;
}

/**
 * Area layouts in world pixels. The server uses them to validate movement;
 * the client uses the same numbers to draw.
 */
export const TOWN = {
  key: 'town',
  name: 'Town Square',
  width: 1280,
  height: 800,
  spawn: { x: 640, y: 600 },
  speed: 180,
  walkable: [{ x: 40, y: 250, w: 1200, h: 520 }],
  obstacles: [
    { x: 575, y: 380, w: 130, h: 100 }, // fountain
    { x: 300, y: 300, w: 60, h: 30 }, // notice board
    { x: 900, y: 300, w: 40, h: 40 }, // lamp post
  ],
  hotspots: [
    { key: 'tidewear', label: 'Tidewear Outfitters', rect: { x: 80, y: 60, w: 300, h: 190 }, action: 'shop:tidewear' },
    { key: 'arcade', label: 'Bayside Arcade', rect: { x: 900, y: 60, w: 300, h: 190 }, action: 'arcade' },
    { key: 'notice', label: 'Notice Board', rect: { x: 288, y: 246, w: 84, h: 90 }, action: 'notice' },
    { key: 'to_beach', label: '← Pebble Beach', rect: { x: 80, y: 700, w: 140, h: 60 }, action: 'area:beach' },
    { key: 'to_home', label: 'Cove Cottages →', rect: { x: 1060, y: 700, w: 140, h: 60 }, action: 'home' },
    { key: 'to_haunted', label: 'Haunted House', rect: { x: 560, y: 680, w: 160, h: 80 }, action: 'area:haunted', event: 'haunted_harbor' },
  ],
} satisfies AreaLayout;

export const BEACH = {
  key: 'beach',
  name: 'Pebble Beach',
  width: 1280,
  height: 800,
  spawn: { x: 640, y: 640 },
  speed: 180,
  walkable: [
    { x: 40, y: 400, w: 1200, h: 370 }, // sand
    { x: 600, y: 140, w: 80, h: 270 }, // pier
  ],
  obstacles: [
    { x: 260, y: 470, w: 90, h: 50 }, // rock pile
    { x: 960, y: 520, w: 70, h: 70 }, // beach umbrella
    { x: 1080, y: 400, w: 160, h: 90 }, // cliffs around the Deep Mine
  ],
  hotspots: [
    { key: 'fishing', label: 'Fishing spot', rect: { x: 590, y: 110, w: 100, h: 70 }, action: 'minigame:fishing' },
    { key: 'mine', label: 'Deep Mine', rect: { x: 1100, y: 400, w: 120, h: 110 }, action: 'mine' },
    { key: 'to_town', label: 'Town Square →', rect: { x: 1060, y: 700, w: 140, h: 60 }, action: 'area:town' },
  ],
} satisfies AreaLayout;

/** Haunted Harbor Halloween only: shops, spooky games and a ghost hunt. */
export const HAUNTED = {
  key: 'haunted',
  name: 'Haunted House',
  width: 1280,
  height: 800,
  spawn: { x: 640, y: 640 },
  speed: 170,
  event: 'haunted_harbor',
  walkable: [{ x: 40, y: 300, w: 1200, h: 470 }],
  obstacles: [
    { x: 585, y: 470, w: 110, h: 60 }, // séance table
    { x: 170, y: 560, w: 130, h: 50 }, // coffin (it's empty, promise)
    { x: 1010, y: 540, w: 60, h: 90 }, // grandfather clock
  ],
  hotspots: [
    { key: 'boo_tique', label: 'The Boo-tique', rect: { x: 60, y: 80, w: 280, h: 220 }, action: 'shop:boo_tique' },
    { key: 'spooky_games', label: 'Spooky Games', rect: { x: 500, y: 70, w: 280, h: 230 }, action: 'arcade:halloween' },
    { key: 'pumpkin_patch', label: 'Pumpkin Patch Market', rect: { x: 940, y: 80, w: 280, h: 220 }, action: 'shop:pumpkin_patch' },
    { key: 'to_town', label: '← Town Square', rect: { x: 560, y: 700, w: 160, h: 60 }, action: 'area:town' },
  ],
} satisfies AreaLayout;

/** The 8 ghosts hiding in the Haunted House. You must walk close to one to catch it. */
export const GHOST_SPOTS = [
  { key: 'attic_ghost', x: 120, y: 350 },
  { key: 'mirror_ghost', x: 1160, y: 350 },
  { key: 'candle_ghost', x: 400, y: 420 },
  { key: 'portrait_ghost', x: 880, y: 420 },
  { key: 'coffin_ghost', x: 330, y: 700 },
  { key: 'clock_ghost', x: 1120, y: 700 },
  { key: 'table_ghost', x: 640, y: 580 },
  { key: 'stair_ghost', x: 760, y: 340 },
] as const;
export const GHOST_REACH = 160;

export const AREAS: Record<string, AreaLayout> = { town: TOWN, beach: BEACH, haunted: HAUNTED };

const inRect = (r: Rect, x: number, y: number) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;

export function isWalkable(area: AreaLayout, x: number, y: number): boolean {
  return area.walkable.some((r) => inRect(r, x, y)) && !area.obstacles.some((o) => inRect(o, x, y));
}

/** Hotspot under a point. Event hotspots only count while their event is live. */
export const hotspotAt = (area: AreaLayout, x: number, y: number, liveEvents: readonly string[] = []) =>
  area.hotspots.find((h) => inRect(h.rect, x, y) && (!h.event || liveEvents.includes(h.event)));

/** Player home room size (pixels) for furniture placement. */
export const HOME_ROOM = { width: 960, height: 600, maxPlacements: 100 } as const;
export const ROTATIONS = [0, 90, 180, 270] as const;

export const layoutSchema = z.object({
  version: z.number().int().min(0),
  placements: z
    .array(
      z.object({
        item: z.string().max(64),
        x: z.number().int().min(0).max(HOME_ROOM.width),
        y: z.number().int().min(0).max(HOME_ROOM.height),
        rotation: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]),
      }),
    )
    .max(HOME_ROOM.maxPlacements),
});
export type LayoutInput = z.infer<typeof layoutSchema>;

export const finishFishingSchema = z.object({ outcome: z.enum(['caught', 'escaped']) });

export interface CatalogItem {
  key: string;
  name: string;
  type: string;
  slot: string | null;
  rarity: string;
  render: Record<string, unknown>;
}

export interface InventoryItemDto extends CatalogItem {
  description: string;
  placeable: boolean;
  category: string;
  quantity: number;
  sellable: boolean;
  sellPrice: number;
}

export interface ShopListingDto {
  listingId: number;
  key: string;
  name: string;
  description: string;
  type: string;
  slot: string | null;
  rarity: string;
  price: number;
  currency: string;
  minLevel: number;
  stock: number | null;
  membersOnly: boolean;
  owned: number;
  render: Record<string, unknown>;
}

export interface ShopDto {
  shop: { key: string; name: string };
  level: number;
  member: boolean;
  balances: Record<string, number>;
  listings: ShopListingDto[];
}

export interface FishingStartDto {
  sessionId: string;
  biteAfterMs: number;
  difficulty: number;
  timeLimitMs: number;
}

export interface FishingResultDto {
  caught: { key: string; name: string; rarity: string; description: string; color: string } | null;
  coins: number;
  xp: number;
  dailyCapReached?: boolean;
  balance?: number | null;
  level?: number;
  leveledUp?: boolean;
}

export interface HomeDto {
  theme: string;
  version: number;
  placements: { item: string; x: number; y: number; rotation: 0 | 90 | 180 | 270 }[];
  furniture: InventoryItemDto[];
}
export * from './pathfinding';

export const EMOTES = ['wave', 'heart', 'laugh', 'dance', 'star'] as const;
export type Emote = (typeof EMOTES)[number];
export const chatSchema = z.object({ text: z.string().min(1).max(200) });
export const quickSchema = z.object({ phraseId: z.number().int().positive() });
export const emoteSchema = z.object({ emote: z.enum(EMOTES) });

export const reportSchema = z.object({
  displayName: z.string().max(32),
  category: z.enum(['unsafe', 'harassment', 'language', 'personal_info', 'scam', 'inappropriate_name', 'cheating', 'other']),
  details: z.string().trim().max(500).optional(),
});

export const arcadeFinishSchema = z.union([
  z.object({ answers: z.array(z.number().int().min(0).max(9)).max(20) }),
  z.object({ moves: z.array(z.number().int().min(0).max(8)).max(400) }),
  z.object({ hits: z.array(z.object({ id: z.number().int().positive(), t: z.number().min(0).max(600_000) })).max(100) }),
]);

// ---------------------------------------------------------------------------
// Driftwood Derby (kart racing). Shared so client and server agree on the track.
// ---------------------------------------------------------------------------

/** Stadium-shaped track: on the track when distance to the centre segment is between inner and outer. */
export const DERBY = {
  width: 1280,
  height: 800,
  cx: 640,
  cy: 400,
  halfLength: 300,
  inner: 190,
  outer: 330,
  laps: 3,
  maxRacers: 4,
  maxSpeed: 260,
  offTrackMaxSpeed: 110,
  accel: 300,
  brake: 380,
  friction: 160,
  turnRate: 2.6,
  raceTimeoutMs: 180_000,
} as const;

export const derbyInputSchema = z.object({
  throttle: z.number().min(-1).max(1),
  steer: z.number().min(-1).max(1),
});

export function derbyDistance(x: number, y: number): number {
  const sx = Math.max(DERBY.cx - DERBY.halfLength, Math.min(DERBY.cx + DERBY.halfLength, x));
  return Math.hypot(x - sx, y - DERBY.cy);
}

export const onDerbyTrack = (x: number, y: number) => {
  const d = derbyDistance(x, y);
  return d >= DERBY.inner && d <= DERBY.outer;
};

/** Clockwise sectors starting from the start line at the top middle. */
export function derbySector(x: number, y: number): 0 | 1 | 2 | 3 {
  if (y < DERBY.cy) return x >= DERBY.cx ? 0 : 3;
  return x >= DERBY.cx ? 1 : 2;
}

export const changePasswordSchema = z.object({ current: z.string().min(1).max(200), next: passwordSchema });
export * from './markdown';
export * from './mine';

// ---------------------------------------------------------------------------
// Farm (behind each player's Cove Cottage)
// ---------------------------------------------------------------------------

/**
 * Farm sizes. Each upgrade makes the field bigger, unlocks a new crop, raises
 * how many coins the market stand pays out per day, and gifts a cottage decoration.
 */
export const FARM_STAGES = [
  { level: 1, size: 3, cost: 0, dailyMarket: 400, unlocks: ['carrot', 'potato'], gift: null },
  { level: 2, size: 5, cost: 500, dailyMarket: 700, unlocks: ['lettuce'], gift: 'furn_hay_bale' },
  { level: 3, size: 8, cost: 750, dailyMarket: 1100, unlocks: ['tomato'], gift: 'furn_veggie_crate' },
  { level: 4, size: 10, cost: 1000, dailyMarket: 1500, unlocks: ['corn'], gift: 'furn_scarecrow' },
  { level: 5, size: 15, cost: 1250, dailyMarket: 2200, unlocks: ['strawberry'], gift: 'furn_sunflower_pot' },
  { level: 6, size: 20, cost: 1500, dailyMarket: 3000, unlocks: ['pumpkin'], gift: 'furn_wheelbarrow' },
  { level: 7, size: 30, cost: 2000, dailyMarket: 4200, unlocks: ['watermelon'], gift: 'furn_farm_windmill' },
  { level: 8, size: 50, cost: 2500, dailyMarket: 6000, unlocks: ['starfruit'], gift: 'furn_golden_tractor' },
] as const;

/** Crops: seed cost to plant one plot, time to grow, and what one harvest sells for. */
export const CROPS = {
  carrot: { name: 'Carrot', seed: 4, growSeconds: 5 * 60, sell: 8, xp: 2, color: '#ef7d2c', leaf: '#3f9b46', level: 1 },
  potato: { name: 'Potato', seed: 6, growSeconds: 15 * 60, sell: 15, xp: 3, color: '#c79a5b', leaf: '#4c8a3c', level: 1 },
  lettuce: { name: 'Lettuce', seed: 8, growSeconds: 30 * 60, sell: 22, xp: 4, color: '#8fd16b', leaf: '#5fae45', level: 2 },
  tomato: { name: 'Tomato', seed: 12, growSeconds: 60 * 60, sell: 36, xp: 6, color: '#e2463a', leaf: '#3f8a3a', level: 3 },
  corn: { name: 'Corn', seed: 15, growSeconds: 2 * 3600, sell: 50, xp: 8, color: '#f2c94c', leaf: '#6ea84a', level: 4 },
  strawberry: { name: 'Strawberry', seed: 20, growSeconds: 4 * 3600, sell: 80, xp: 12, color: '#e8384f', leaf: '#3c9a4a', level: 5 },
  pumpkin: { name: 'Pumpkin', seed: 30, growSeconds: 8 * 3600, sell: 135, xp: 18, color: '#f08a24', leaf: '#4f8a2a', level: 6 },
  watermelon: { name: 'Watermelon', seed: 40, growSeconds: 12 * 3600, sell: 200, xp: 25, color: '#3c9a4a', leaf: '#2f7a34', level: 7 },
  starfruit: { name: 'Starfruit', seed: 60, growSeconds: 24 * 3600, sell: 340, xp: 40, color: '#f6d54a', leaf: '#5aa04a', level: 8 },
} as const;
export type CropKey = keyof typeof CROPS;
export const cropItemKey = (c: CropKey) => `crop_${c}`;
export const farmStage = (level: number) => FARM_STAGES[Math.min(Math.max(level, 1), FARM_STAGES.length) - 1]!;

export interface FarmPlotDto {
  crop: CropKey;
  plantedAt: string;
  readyAt: string;
}
export interface FarmDto {
  level: number;
  size: number;
  /** Row-major, size × size; null = empty soil. */
  plots: (FarmPlotDto | null)[];
  serverTime: string;
  next: { level: number; size: number; cost: number; unlocks: string[] } | null;
  market: { soldToday: number; dailyLimit: number };
  /** Crops in the backpack, ready to sell. */
  barn: Partial<Record<CropKey, number>>;
}
